import React, { useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import Section from 'core/components/section';

const SECTIONS = [
  { id: 'branch-data', label: 'Branch Data Changes' },
  { id: 'cd-request', label: 'ClarionDoor Request XML' }
];

const useStyles = makeStyles((theme) => ({
  navBar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center'
  },
  jumpToLabel: {
    marginRight: theme.spacing(1)
  }
}));

const getInitialAccumulatorValue = (removedChanges, addedChanges) => {
  const obj = {};
  Object.keys(removedChanges).forEach((key) => {
    obj[key] = null;
  });
  Object.keys(addedChanges).forEach((key) => {
    obj[key] = null;
  });
  return obj;
};

const sortedObj = (obj, defaults) =>
  Object.keys(obj)
    .sort()
    .reduce((accumulator, key) => {
      accumulator[key] = obj[key];
      return accumulator;
    }, defaults);

const PreviewClarionDoorRequestDifference = ({ policyPreviewId, policyId }) => {
  const classes = useStyles();
  const {
    account: {
      policies: {
        policy: { getClarionDoorRequestDiff }
      }
    }
  } = useStore();

  const [data, setData] = useState();
  const [gqlError, setGqlError] = useState();
  const [loading, setLoading] = useState(false);

  if (!loading && !data) {
    setLoading(true);
    getClarionDoorRequestDiff({ policyId, policyPreviewId }).then(({ requests, error }) => {
      setLoading(false);
      if (requests) {
        setData({ ...requests, diff: JSON.parse(requests?.diff ?? {}) });
      }
      if (error) {
        setGqlError(error);
      }
    });
  }

  const sortedRemovedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.removedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );
  const sortedAddedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.addedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );

  if (loading) {
    return <Loading />;
  }

  const hasRequestXml = data?.xmlBeforePreview?.success && data?.xmlAfterPreview?.success;

  return (
    <>
      <Grid container spacing={2} className={classes.navBar}>
        <Grid item>
          <Label type="darkGreenSmallBold" className={classes.jumpToLabel}>
            Jump to:
          </Label>
        </Grid>
        {SECTIONS.map(({ id, label }) => (
          <Grid item key={id}>
            <Button variant="contained" color="secondary" href={`#${id}`}>
              {label}
            </Button>
          </Grid>
        ))}
      </Grid>

      <Section title="Branch Data Changes" type="Section" id="branch-data">
        {data?.diff ? (
          <ReactDiffViewer
            oldValue={JSON.stringify(sortedRemovedChanges, null, 4)}
            newValue={JSON.stringify(sortedAddedChanges, null, 4)}
            splitView
            compareMethod={DiffMethod.WORDS}
            leftTitle={'Branch Data Before Preview'}
            rightTitle={'Branch Data After Preview'}
          />
        ) : (
          <>
            <Label>Error getting Branch Data diff.</Label>
            {gqlError && <Label>{gqlError}</Label>}
          </>
        )}
      </Section>

      <Section title="ClarionDoor Request XML" type="Section" id="cd-request">
        {hasRequestXml ? (
          <ReactDiffViewer
            oldValue={data.xmlBeforePreview.data}
            newValue={data.xmlAfterPreview.data}
            splitView
            compareMethod={DiffMethod.WORDS}
            leftTitle={`ClarionDoor Request Before Preview - ID: ${data?.requestIdBeforePreview}`}
            rightTitle={`ClarionDoor Request After Preview - ID: ${data?.requestIdAfterPreview}`}
          />
        ) : (
          <>
            <Label>Error getting ClarionDoor request data.</Label>
            <Label>{data?.xmlBeforePreview?.message || data?.xmlAfterPreview?.message || gqlError}</Label>
          </>
        )}
      </Section>
    </>
  );
};

export default PreviewClarionDoorRequestDifference;
